import { HealthDataHeaderCard } from '@/app/components/health-data/components/health-data-header/components/health-data-header-card';
import { HealthDataHeaderTitle } from '@/app/components/health-data/components/health-data-header/components/health-data-header-title';
import HealthDataTabs from '@/app/components/health-data/components/health-data-tabs';
import { Tab, TabType } from '@/app/components/health-data/types';
import { useModal } from '@/app/components/questionnaireV2/components/survey-item/components/tooltip/tooltip-modal';
import HeaderBackground from '@/components/ui/header-background/header-background';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useDashboardController from '../../dashboard.controller';
import DashboardHeaderCard from './components/dashboard-header-card';
import HeaderTooltip from './components/header-tooltip/header-tooltip';
import { getRiskConfigData } from './utils/risk-config';

interface DashboardHeaderProps {
  className?: string;
  children?: React.ReactNode;
  /** Hero content to render in the left side of the card */
  heroContent?: React.ReactNode;
  /** Tab configuration - when provided, renders tabs below the card */
  tabsConfig?: {
    activeTab: Tab;
    onTabChange: (tabId: TabType) => void;
    tabs: Tab[];
  };
}

export default function DashboardHeaderV2({ className = '', children, heroContent, tabsConfig }: DashboardHeaderProps) {
  const analytics = useAnalyticsCalls();
  const { isOpen, openModal, closeModal } = useModal();

  const { name, methodologyUrl, populationAverage, lifetimeRisk, benchmark, userGender, biologicalAge } =
    useDashboardController();

  // Determine if we're showing the risk insights view based on active tab
  const isRiskInsightsView = tabsConfig?.activeTab.id === 'risk-insights';

  let riskConfig;
  if (isRiskInsightsView) {
    riskConfig = getRiskConfigData(benchmark, lifetimeRisk, populationAverage, userGender);
  }

  const handleBadgeClick = () => {
    openModal();
    analytics.callModalViewed('Lifetime Risk Score');
  };

  return (
    <div
      className={`dashboard-reset relative overflow-hidden bg-[#0a0a0a] ${className}`}
      role="region"
      aria-label={isRiskInsightsView ? 'Cancer risk dashboard header' : 'Health data dashboard header'}
    >
      <HeaderBackground blur={40} />
      {isRiskInsightsView ? (
        <DashboardHeaderCard
          name={name}
          benchmark={benchmark}
          currentRisk={lifetimeRisk}
          averageRisk={populationAverage}
          methodologyUrl={methodologyUrl}
          userGender={userGender}
          onBadgeClick={handleBadgeClick}
          biologicalAge={biologicalAge}
        />
      ) : (
        <HealthDataHeaderCard heroContent={heroContent} titleContent={<HealthDataHeaderTitle />}>
          {children}
        </HealthDataHeaderCard>
      )}

      {/* Render tabs below the cards */}
      {tabsConfig && (
        <div className="relative z-10 mx-auto max-w-7xl px-4 pb-8 sm:px-6 md:pt-6 lg:px-8">
          <div className="mx-auto w-[335px]">
            <HealthDataTabs
              activeTab={tabsConfig.activeTab}
              onTabChange={tabsConfig.onTabChange}
              tabs={tabsConfig.tabs}
            />
          </div>
        </div>
      )}

      {/* Risk insights tooltip */}
      {isRiskInsightsView && riskConfig && (
        <HeaderTooltip
          isOpen={isOpen}
          onClose={closeModal}
          title={riskConfig.title}
          description={riskConfig.tooltipDescription}
          iconColor={riskConfig.cssClass}
        />
      )}
    </div>
  );
}
